import { z } from "zod";
import { catalogueLocale } from "../catalog/vocabulary";
import { calendarDay } from "./profile";
import { progressSpan } from "./progress";
import { calendarMonth, isoWeek } from "./retrospective";
/**
 * `GET /me/report?period=week|month[&week=YYYY-Www|&month=YYYY-MM]&locale` —
 * the weekly and monthly progress report (`D252`): the analytical companion
 * of the Retrospectiva (`D204`), which tells the same span as a story.
 *
 * ── THE DEFINITIONS LIVE IN ONE PLACE ───────────────────────────────────────
 * `packages/modules/training/src/core/model/progress-report.ts`. In short:
 * - the spans are the Retrospectiva's: an ISO week (Monday to Sunday, UTC)
 *   or a UTC calendar month, the last ENDED one by default. A span still
 *   running answers `ready: false` — Progress is the dashboard for it;
 * - `ready` is derived, never stored: the span has ended and the person has
 *   an ended session that started before its end. An ended span with no
 *   workout in it IS ready, and says so with zeros;
 * - every count reads working sets only (`D209`), sets per muscle are
 *   `D182`'s fractional sets, and a unilateral set counts both sides in the
 *   volume (`D232`);
 * - the baseline is always the FOUR spans before this one.
 *
 * ── KEYS AND NAMES, NEVER SENTENCES ─────────────────────────────────────────
 * Every string here is an id, a word of a closed vocabulary, or a localised
 * catalogue name. The sentences are the client's copy (`D27`).
 */
/** The report's spans. A year has the Retrospectiva's story and no report. */
export const REPORT_PERIODS = ["week", "month"];
export const reportPeriod = z.enum(REPORT_PERIODS);
/** STRICT: an unknown key is 422 `request.invalid_query`. */
export const reportQuery = z
    .object({
    /** Absent means `week`. */
    period: reportPeriod.optional(),
    /** Read only for `week`; absent, the last ended ISO week. A week number
     *  the year does not have is `retrospective.week_invalid`. */
    week: isoWeek.optional(),
    /** Read only for `month`; absent, the last ended month. A month number
     *  outside 01–12 is `retrospective.month_invalid`. */
    month: calendarMonth.optional(),
    /** The locale muscle and exercise names are in. English when absent. */
    locale: catalogueLocale.optional(),
})
    .strict();
/** The person's sessions against the training config's days per week. */
export const reportConsistency = z.object({
    /** Workouts started in the span — an abandoned session is not one (`D177`). */
    done: z.int().nonnegative(),
    /** The training config's CURRENT days per week; `null` without a config.
     *  Weekly, also for a month: a monthly target is not one any plan set. */
    plannedPerWeek: z.int().nonnegative().nullable(),
    /** The span's length in weeks: 1 for a week, days ÷ 7 for a month. */
    weeks: z.number().positive(),
});
export const reportMuscle = z.object({
    muscleId: z.int().positive(),
    name: z.string().min(1),
    /** The span's fractional sets (`D182`), to the half set. */
    sets: z.number().nonnegative(),
    /** The same per week (`sets ÷ weeks`), to the half set — what the target
     *  is compared with. Equal to `sets` for a week. */
    weeklySets: z.number().nonnegative(),
    /** The ACTIVE plan's fractional weekly sets for the muscle, to the half;
     *  `null` with no active plan. The plan at request time, not the span's. */
    targetSets: z.number().nonnegative().nullable(),
    /** The muscle's part of the span's fractional sets, 0–1. */
    share: z.number().min(0).max(1),
    /** Its part of the four spans before, pooled, 0–1; `null` when they hold
     *  no working set. */
    previousShare: z.number().min(0).max(1).nullable(),
});
/** What the exercise's trend says, and `insufficient` when too few spans
 *  hold it to say anything. */
export const REPORT_VERDICTS = [
    "progressing",
    "holding",
    "regressing",
    "insufficient",
];
export const reportVerdict = z.enum(REPORT_VERDICTS);
/** `e1rm`: the best Epley estimate per span, in kg. `reps`: the most reps
 *  in a set per span, for an exercise logged without a load. */
export const reportMetric = z.enum(["e1rm", "reps"]);
export const reportExercise = z.object({
    exerciseId: z.string().min(1),
    name: z.string().min(1),
    verdict: reportVerdict,
    metric: reportMetric,
    /** The span's best, in the metric's unit. */
    current: z.number().nonnegative(),
    /** The trend's change across the spans it read, in percent of their
     *  median; `null` exactly when `verdict` is `insufficient`. */
    changePct: z.number().nullable(),
    /** How many of the five spans (this one and the four before) hold it. */
    spans: z.int().positive(),
    /** How many spans the trend runs over, first point to this one. */
    spanLength: z.int().nonnegative(),
});
/** A record beaten in the span: the session's best estimate against every
 *  earlier one — the session summary's rule, first-ever lifts excluded. */
export const reportRecord = z.object({
    exerciseId: z.string().min(1),
    name: z.string().min(1),
    sessionId: z.string().min(1),
    /** The UTC day the session started. */
    day: calendarDay,
    e1rmKg: z.number().nonnegative(),
    /** The best estimate before that session. */
    previousE1rmKg: z.number().nonnegative(),
});
export const reportBody = z.object({
    /** The span's workouts, minutes, working sets and volume. */
    totals: progressSpan,
    /** The same for the span before, whole. */
    previous: progressSpan,
    consistency: reportConsistency,
    /** Every muscle with sets in the span or the four before, or a plan
     *  target; sets descending, then `muscleId`. */
    muscles: z.array(reportMuscle),
    /** How many of the four spans before hold a working set — what
     *  `previousShare` is the average of. */
    baselineSpans: z.int().min(0).max(4),
    /** Every exercise with a point in the span; by verdict, then by change. */
    exercises: z.array(reportExercise),
    /** Oldest first. */
    records: z.array(reportRecord),
});
export const reportOutput = z.object({
    period: reportPeriod,
    /** `YYYY-Www`, for a week. */
    week: isoWeek.optional(),
    /** `YYYY-MM`, for a month. */
    month: calendarMonth.optional(),
    /** The span's first and last UTC day. */
    from: calendarDay,
    to: calendarDay,
    /** Ended, and an ended session started before its end. */
    ready: z.boolean(),
    /** The span before, by name, when the person trained before this one;
     *  `null` at the first. */
    previousName: z.string().nullable(),
    /** The span after, by name, when it has ended; `null` at the latest. */
    nextName: z.string().nullable(),
    /** `null` exactly when `ready` is false. */
    report: reportBody.nullable(),
});
//# sourceMappingURL=report.js.map